import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { HealthCheck, SimpleClusterForm } from "@/types";
// eslint-disable-next-line
// @ts-ignore
import debounce from "lodash/debounce";

const ipv4Regex =
  /^(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])){3}$/;

const IPv4SubnetRegex =
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})(?:\/(?:[0-9]|[12]\d|3[0-2]))?$/;

// const IPv6SubnetPattern = [
//   "^(",
//   "(?:[A-Fa-f0-9]{1,4}:){7}[A-Fa-f0-9]{1,4}|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,7}:|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,6}:[A-Fa-f0-9]{1,4}|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,5}(?::[A-Fa-f0-9]{1,4}){1,2}|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,4}(?::[A-Fa-f0-9]{1,4}){1,3}|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,3}(?::[A-Fa-f0-9]{1,4}){1,4}|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,2}(?::[A-Fa-f0-9]{1,4}){1,5}|",
//   "[A-Fa-f0-9]{1,4}:(?:(?::[A-Fa-f0-9]{1,4}){1,6})|",
//   ":(?:(?::[A-Fa-f0-9]{1,4}){1,7}|:)|",
//   "fe80:(?::[A-Fa-f0-9]{0,4}){0,4}%[0-9a-zA-Z]{1,}|",
//   "::(?:ffff(?::0{1,4}){0,1}:){0,1}",
//   "(?:(?:25[0-5]|(?:2[0-4]\\d|1\\d\\d|[1-9]?\\d))\\.){3}",
//   "(?:25[0-5]|(?:2[0-4]\\d|1\\d\\d|[1-9]?\\d))|",
//   "(?:[A-Fa-f0-9]{1,4}:){1,4}:",
//   "(?:(?:25[0-5]|(?:2[0-4]\\d|1\\d\\d|[1-9]?\\d))\\.){3}",
//   "(?:25[0-5]|(?:2[0-4]\\d|1\\d\\d|[1-9]?\\d))",
//   ")(?:\\/(?:[0-9]|[1-9]\\d|1[01]\\d|12[0-8]))?$"
// ].join("");
// const IPv6SubnetRegex = new RegExp(IPv6SubnetPattern);

const GithubIdRegex = /^(?!-)(?!.*--)[A-Za-z0-9-]{1,39}(?<!-)$/;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getFormikEnvKeysValues = (values: SimpleClusterForm) => {
  const normKeys = Object.keys(values).filter((key) => /^env_normal_key_\d+$/.test(key));
  const normValues = Object.keys(values).filter((key) => /^env_normal_value_\d+$/.test(key));
  const secretKeys = Object.keys(values).filter((key) => /^env_private_key_\d+$/.test(key));
  const secretValues = Object.keys(values).filter((key) => /^env_private_value_\d+$/.test(key));
  return { normKeys, normValues, secretKeys, secretValues };
};

export const validatePort = (port?: number, isRequired?: boolean) => {
  if (!isRequired && port !== 0 && !port) {
    return "";
  }
  return port === 0 || (port && port >= 0 && port <= 65535)
    ? ""
    : "Port must be between 0 and 65535";
};

export const isValidIP = (ip?: string, isRequired?: boolean) => {
  if (!isRequired && !ip) {
    return "";
  }
  return ip && ipv4Regex.test(ip) ? "" : "Invalid IP address format";
};

export const filterProps = (
  data: { [k: string]: unknown },
  filterKey?: string[]
): { [p: string]: unknown } =>
  Object.fromEntries(
    Object.entries(data).filter(([key, value]) => (filterKey ? !filterKey.includes(key) : value))
  );

export const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
};

export const isValidSSHKey = (sshKey?: string) => {
  if (!sshKey) {
    return false;
  }
  /* https://github.com/nemchik/ssh-key-regex */
  const sshCombinedPattern =
    /^(ssh-dss AAAAB3NzaC1kc3|ecdsa-sha2-nistp256 AAAAE2VjZHNhLXNoYTItbmlzdHAyNT|ecdsa-sha2-nistp384 AAAAE2VjZHNhLXNoYTItbmlzdHAzOD|ecdsa-sha2-nistp521 AAAAE2VjZHNhLXNoYTItbmlzdHA1Mj|sk-ecdsa-sha2-nistp256@openssh.com AAAAInNrLWVjZHNhLXNoYTItbmlzdHAyNTZAb3BlbnNzaC5jb2|ssh-ed25519 AAAAC3NzaC1lZDI1NTE5|sk-ssh-ed25519@openssh.com AAAAGnNrLXNzaC1lZDI1NTE5QG9wZW5zc2guY29t|ssh-rsa AAAAB3NzaC1yc2)[0-9A-Za-z+/]+[=]{0,3}(\s.*)?$/;

  // const sshRsaPattern = /^ssh-rsa\s+[A-Za-z0-9+/=]+\s*(.+)?$/;
  // const sshEd25519Pattern = /^ssh-ed25519\s+[A-Za-z0-9+/=]+\s*(.+)?$/;
  // const sshEcdsaPattern = /^ecdsa-sha2-nistp(256|384|521)\s+[A-Za-z0-9+/=]+\s*(.+)?$/;
  // return (
  //   sshRsaPattern.test(sshKey) || sshEd25519Pattern.test(sshKey) || sshEcdsaPattern.test(sshKey)
  // );
  return sshCombinedPattern.test(sshKey);
};

export const isDeprecatedSSHKey = (sshKey?: string) => {
  if (!sshKey) {
    return false;
  }

  const deprecatedSshPattern = /^(ssh-dss AAAAB3NzaC1kc3)[0-9A-Za-z+/]+[=]{0,3}(\s.*)?$/;
  return deprecatedSshPattern.test(sshKey);
};

export const isValidJsonString = (data: string) => {
  try {
    JSON.parse(data);
  } catch (e) {
    return false;
  }
  return true;
};

const checkGithubId = async (githubIds?: string) => {
  if (!githubIds) {
    return "Required";
  }

  const multipleIds = githubIds.split(",").map((id) => id.trim());

  for (const githubId of multipleIds) {
    if (!githubId) {
      return "GitHub ID cannot be empty";
    }

    if (!GithubIdRegex.test(githubId)) {
      return `Invalid GitHub ID format: ${githubId}`;
    }

    try {
      const response = await fetch(`https://api.github.com/users/${githubId}`);
      if (response.status === 404) {
        return `GitHub ID does not exist: ${githubId}`;
      }
      // 403 error is shown when rate limit exceeds for unauthenticated requests
      // so skipping validation in that case
      if (response.status === 403) {
        return undefined;
      }
      if (!response.ok) {
        return `Error checking GitHub ID (${githubId})`;
      }
    } catch (err) {
      return `Network error while checking ${githubId}`;
    }
  }

  return undefined;
};

export const isValidGithubId = debounce(checkGithubId, 500);

export function isValidIPorSubnet(input: string): boolean {
  return IPv4SubnetRegex.test(input); //|| IPv6SubnetRegex.test(input);
}

export const isValidName = (name?: string) => {
  if (!name) {
    return "Required";
  }

  if (name.length > 32) {
    return "Name must be between 1 and 32 characters long";
  }

  const nameRegex = /^[A-Za-z0-9_-]+$/;
  return nameRegex.test(name) ? undefined : "Letters, numbers, _ and - only";
};

// Reserved for when interval/timeout/grace period are user-configurable again.
// const healthCheckSecondsFromForm = (v: unknown, fallback: number) => {
//   if (typeof v === "number" && Number.isFinite(v)) return Math.round(v);
//   if (typeof v === "string" && v.trim() !== "") {
//     const n = parseInt(v.trim(), 10);
//     if (Number.isFinite(n)) return n;
//   }
//   return fallback;
// };

export const secondsToCaasDuration = (seconds: number): string =>
  `${Math.max(0, Math.round(seconds))}s`;

/** Parses CaaS duration strings (e.g. `10s`, `2m`) to whole seconds; supports legacy numeric seconds. */
export const parseCaasDurationToSeconds = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value);
  }
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const match = /^(\d+(?:\.\d+)?)(ms|s|m|h)$/.exec(trimmed);
  if (!match) return undefined;
  const amount = parseFloat(match[1]);
  const unit = match[2];
  switch (unit) {
    case "ms":
      return Math.round(amount / 1000);
    case "s":
      return Math.round(amount);
    case "m":
      return Math.round(amount * 60);
    case "h":
      return Math.round(amount * 3600);
    default:
      return undefined;
  }
};

/** Builds health_check for CaaS create/update. Only `path` is sent; interval/timeout/grace_period are omitted. */
export const buildCaasHealthCheckForApi = (
  values: SimpleClusterForm
): HealthCheck | undefined => {
  const path = typeof values.healthCheckPath === "string" ? values.healthCheckPath.trim() : "";
  if (!path) return undefined;

  return {
    path
    // interval / timeout / grace_period intentionally omitted — backend defaults apply
    // interval: secondsToCaasDuration(healthCheckSecondsFromForm(values.healthCheckIntervalSeconds, 10)),
    // timeout: secondsToCaasDuration(healthCheckSecondsFromForm(values.healthCheckTimeoutSeconds, 10)),
    // grace_period: secondsToCaasDuration(healthCheckSecondsFromForm(values.healthCheckGracePeriodSeconds, 60))
  };
};
